import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { summaryReportsService, reportsRepositoryService } from '../services';

const STATUS_TEXT = {
  ready: 'Generated from the linked Stocktake source and saved as the Stock Audit Report.',
  not_generated: 'Not generated yet. Publishing is blocked until it is generated.',
  generating: 'Generating from the Stocktake source… this can take up to a minute.',
  failed: 'Generation failed. Publishing is blocked until a retry succeeds.',
  source_stale: 'The Stocktake calculation changed. Refresh the source, then regenerate.',
};

export const StockAuditAttachmentPanel = ({ reportId, canEdit }) => {
  const [att, setAtt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try { setAtt(await summaryReportsService.getStockAuditAttachment(reportId)); } catch (_) { setAtt(null); }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (att?.status !== 'generating') return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [att?.status, load]);

  if (!att?.linked) return null;

  const generate = async () => {
    setBusy(true);
    try { setAtt(await summaryReportsService.generateStockAuditAttachment(reportId)); } finally { setBusy(false); }
  };

  const openDocument = async () => {
    setDownloading(true);
    try {
      await reportsRepositoryService.downloadVersion(
        att.version_id,
        `Full_Stock_Audit_Report_v${att.version_number || 1}.pdf`
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to open the document');
    } finally {
      setDownloading(false);
    }
  };

  const ok = att.status === 'ready';
  const tone = ok ? 'border-green-200 bg-green-50' : att.status === 'generating' ? 'border-slate-200 bg-slate-50' : 'border-amber-300 bg-amber-50';
  const canGenerate = canEdit && ['not_generated', 'failed'].includes(att.status);

  return (
    <div className={`mt-6 rounded border px-4 py-3 text-sm ${tone}`} data-testid="stock-audit-attachment-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          {ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600" />
            : att.status === 'generating' ? <Loader2 className="h-4 w-4 mt-0.5 animate-spin" />
            : att.status === 'failed' || att.status === 'source_stale' ? <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600" />
            : <FileText className="h-4 w-4 mt-0.5" />}
          <div>
            <div className="font-medium">Full Stock Audit Report (Beverage)</div>
            <div data-testid="stock-audit-attachment-status">{STATUS_TEXT[att.status]}</div>
            {att.error && <div className="text-xs text-amber-800" data-testid="stock-audit-attachment-error">{att.error}</div>}
            <div className="text-xs text-slate-500 mt-1">
              Source: Stocktake snapshot #{att.source?.calc_snapshot_id} v{att.source?.version} · {att.source?.sha256?.slice(0, 12)}…
              {ok && ` · Version ${att.version_number}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ok && att.version_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={openDocument}
              disabled={downloading}
              data-testid="stock-audit-attachment-open-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Open Document
            </Button>
          )}
          {canGenerate && (
            <Button size="sm" onClick={generate} disabled={busy} data-testid="stock-audit-attachment-generate-btn">
              {att.status === 'failed' ? <><RefreshCw className="h-4 w-4 mr-1" />Retry</> : 'Generate'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
